/**
 * AgentsList.tsx — Automation / All Agents page (Closr style)
 *
 * P1: colors/sizes flow from src/theme/tokens.ts; sizes sit on the P1 scale.
 */
import { useState } from 'react'
import { Plus, Search, Play, Edit2, Eye } from 'lucide-react'
import { SectionTitle, StatusBadge, LoadingState, ErrorState } from '../DashboardShared'
import { useAsync, fetchAgents } from '../api'
import { toast } from 'sonner'
import { danger, success, warning } from '../../../theme/tokens'

export function AgentsList() {
  const [filter, setFilter] = useState<'all' | 'active' | 'paused' | 'archived'>('all')
  const [search, setSearch] = useState('')
  const { data, loading, error } = useAsync(fetchAgents, [])
  const agents = data ?? []

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const filtered = agents.filter(a => {
    if (filter !== 'all' && a.status !== filter) return false
    if (search && !a.name.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  return (
    <div className="page-enter flex flex-col gap-5">
      <SectionTitle
        title="All Agents"
        subtitle={`${agents.filter(a => a.status === 'active').length} active agents`}
        action={
          <button className="k-btn k-btn--lg inline-flex items-center gap-2" onClick={() => toast.info('Agent creation coming soon')}>
            <Plus size={14} /> New Agent
          </button>
        }
      />

      <div className="k-card">
        <div className="flex items-center gap-3 px-5 py-3 border-b border-edge-row">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search agents…"
              className="h-8 pl-8 pr-3 text-13 bg-surface-input border border-edge-control rounded-control text-ink-body placeholder:text-ink-faint focus:outline-none focus:border-accent w-52"
            />
          </div>
          <div className="flex gap-1">
            {(['all', 'active', 'paused', 'archived'] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-2 rounded-full text-12 font-medium transition-colors capitalize ${filter === f ? 'bg-ink-primary text-ink-on-accent' : 'text-ink-secondary hover:bg-surface-nav-hover'}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <table className="closr-table dash-table w-full">
          <thead>
            <tr>
              <th className="text-left">Name</th>
              <th className="text-left">Status</th>
              <th className="text-left">Model</th>
              <th className="text-left">Schedule</th>
              <th className="text-left">Last Run</th>
              <th className="text-left">Success Rate</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(agent => (
              <tr key={agent.id}>
                <td className="font-medium text-ink-primary">{agent.name}</td>
                <td><StatusBadge status={agent.status} /></td>
                <td className="font-mono text-12 text-ink-secondary">{agent.model}</td>
                <td className="text-12 text-ink-secondary">{agent.schedule}</td>
                <td className="text-12 text-ink-faint">{agent.lastRun}</td>
                <td>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-1.5 bg-track rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${agent.successRate}%`,
                          background: agent.successRate >= 90 ? success.DEFAULT : agent.successRate >= 75 ? warning.DEFAULT : danger.DEFAULT
                        }}
                      />
                    </div>
                    <span className="text-12 font-mono text-ink-body">{agent.successRate}%</span>
                  </div>
                </td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => toast.info('Edit coming soon')} className="p-2 rounded hover:bg-surface-nav-hover text-ink-faint hover:text-ink-body transition-colors"><Edit2 size={13} /></button>
                    <button onClick={() => toast.info('Run triggered')} className="p-2 rounded hover:bg-surface-nav-hover text-ink-faint hover:text-ink-body transition-colors"><Play size={13} /></button>
                    <button onClick={() => toast.info('View runs coming soon')} className="p-2 rounded hover:bg-surface-nav-hover text-ink-faint hover:text-ink-body transition-colors"><Eye size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
